import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@shared/components/Button';
import Layout from '@shared/components/Layout';
import {
  getAuthMfaGenericInteractionScreenTitle,
  publishButtonClickedEvent,
  type AuthMfaFlow,
} from '@shared/utils/analytics/events';
import { getMfaScreenLayoutProps, MfaScreenLayoutProps } from './layout';

interface Props extends MfaScreenLayoutProps {
  onClickSms: () => Promise<void>;
  onClickTotp: () => Promise<void>;
  mfaFlow?: AuthMfaFlow;
  errorText?: string;
}

const MfaMethodSelectionScreen: React.FC<Props> = ({
  onClickSms,
  onClickTotp,
  mfaFlow,
  errorText,
  variant,
  bannerImageDesktop,
  hideLogo,
  headerClasses,
}) => {
  const { t } = useTranslation();
  const [loadingMethod, setLoadingMethod] = useState<
    'sms' | 'totp' | undefined
  >();
  const layoutProps = getMfaScreenLayoutProps({
    variant,
    bannerImageDesktop,
    hideLogo,
    headerClasses,
  });

  const handleMethodClick = async (
    method: 'sms' | 'totp',
    handler: () => Promise<void>
  ) => {
    if (mfaFlow) {
      publishButtonClickedEvent(
        method === 'sms' ? 'SMS MFA Method' : 'TOTP MFA Method',
        getAuthMfaGenericInteractionScreenTitle(mfaFlow)
      );
    }

    setLoadingMethod(method);
    try {
      await handler();
    } finally {
      setLoadingMethod(undefined);
    }
  };

  return (
    <Layout
      heading={t('Select a method to verify your identity')}
      {...layoutProps}
    >
      <div className="space-y-3">
        <Button
          onClick={() => handleMethodClick('sms', onClickSms)}
          label={t('SMS')}
          state={loadingMethod === 'sms' ? 'loading' : 'default'}
        />
        <Button
          onClick={() => handleMethodClick('totp', onClickTotp)}
          label={t('Google Authenticator or similar')}
          state={loadingMethod === 'totp' ? 'loading' : 'default'}
        />
        {errorText && (
          <p className="text-red-600 text-center text-sm">{errorText}</p>
        )}
      </div>
    </Layout>
  );
};

export default MfaMethodSelectionScreen;
